import React from 'react';
import { UserProfile } from '../types';

interface AvatarRequirement {
  type: 'quests' | 'streak' | 'level' | 'currency' | 'achievements';
  value: number;
  description: string;
}

interface AvatarData {
  name: string;
  path: string;
  requirements?: AvatarRequirement[];
  isDefault?: boolean;
}

interface AvatarSelectorProps {
  selectedAvatar?: string;
  onAvatarSelect: (avatarPath: string) => void;
  userProfile: UserProfile;
  className?: string;
}

interface AvatarSelectorState {
  previewAvatar?: string; // For showing locked avatar requirements
}

const AvatarSelector: React.FC<AvatarSelectorProps> = ({ 
  selectedAvatar, 
  onAvatarSelect, 
  userProfile,
  className = '' 
}) => {
  const [previewAvatar, setPreviewAvatar] = React.useState<string | undefined>(selectedAvatar);

  // Sync preview with selected avatar when it changes
  React.useEffect(() => {
    setPreviewAvatar(selectedAvatar);
  }, [selectedAvatar]);
  // Available avatar files with unlock requirements
  const avatars: AvatarData[] = [
    {
      name: 'The Arc Oracle',
      path: '/avatars/TheArcOracle.png',
      isDefault: true
    },
    {
      name: 'The Crimson Berserker',
      path: '/avatars/TheCrimsonBerserker.png',
      requirements: [
        { type: 'quests', value: 50, description: 'Complete 50 Quests' }
      ]
    },
    {
      name: 'The Gatebreaker',
      path: '/avatars/TheGatebreaker.png',
      requirements: [
        { type: 'level', value: 5, description: 'Reach Level 5' }
      ]
    },
    {
      name: 'The Lightbringer',
      path: '/avatars/TheLightbringer.png',
      requirements: [
        { type: 'streak', value: 7, description: 'Maintain 7-Day Streak' }
      ]
    },
    {
      name: 'The Monarch of Frost',
      path: '/avatars/TheMonarchofFrost.png',
      requirements: [
        { type: 'level', value: 10, description: 'Reach Level 10' },
        { type: 'quests', value: 100, description: 'Complete 100 Quests' }
      ]
    },
    {
      name: 'The Necro Warden',
      path: '/avatars/TheNecroWarden.png',
      requirements: [
        { type: 'streak', value: 14, description: 'Maintain 14-Day Streak' },
        { type: 'level', value: 8, description: 'Reach Level 8' }
      ]
    },
    {
      name: 'The Shadow Assassin',
      path: '/avatars/TheShadowAssassin.png',
      requirements: [
        { type: 'currency', value: 500, description: 'Earn 500 Gold' }
      ]
    },
    {
      name: "The Shadow Monarch's Echo",
      path: '/avatars/TheShadowMonarchEcho.png',
      requirements: [
        { type: 'level', value: 15, description: 'Reach Level 15' },
        { type: 'quests', value: 200, description: 'Complete 200 Quests' },
        { type: 'streak', value: 21, description: 'Maintain 21-Day Streak' }
      ]
    },
    {
      name: 'The Starlight Vindicator',
      path: '/avatars/TheStarlightVindicator.png',
      requirements: [
        { type: 'level', value: 20, description: 'Reach Level 20' },
        { type: 'currency', value: 2500, description: 'Earn 2500 Gold' }
      ]
    },
    {
      name: 'The Void Huntress',
      path: '/avatars/TheVoidHuntress.png',
      requirements: [
        { type: 'level', value: 25, description: 'Reach Level 25' },
        { type: 'quests', value: 500, description: 'Complete 500 Quests' },
        { type: 'streak', value: 30, description: 'Maintain 30-Day Streak' },
        { type: 'currency', value: 5000, description: 'Earn 5000 Gold' }
      ]
    }
  ];

  // Function to check if user meets avatar requirements
  const isAvatarUnlocked = (avatar: AvatarData): boolean => {
    if (avatar.isDefault) return true;
    if (!avatar.requirements) return true;

    return avatar.requirements.every(req => {
      switch (req.type) {
        case 'level':
          return userProfile.level >= req.value;
        case 'quests':
          return userProfile.lifetimeStats.totalQuestsCompleted >= req.value;
        case 'streak':
          return userProfile.longestStreak >= req.value;
        case 'currency':
          return userProfile.lifetimeStats.totalCurrencyEarned >= req.value;
        case 'achievements':
          // We'll need to implement achievement counting later
          return false;
        default:
          return false;
      }
    });
  };

  return (
    <div className={`space-y-4 ${className}`}>
      <div className="mb-4 sm:mb-6">
        <label className="block text-xs sm:text-sm font-rajdhani text-gray-400 uppercase mb-2">
          Avatar Selection
        </label>
        <p className="text-xs sm:text-sm text-gray-500 font-rajdhani">
          Choose your Hunter's appearance and identity
        </p>
      </div>
      
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-2 sm:gap-3">
        {avatars.map((avatar) => {
          const isSelected = selectedAvatar === avatar.path;
          const isPreviewed = previewAvatar === avatar.path;
          const isUnlocked = isAvatarUnlocked(avatar);
          
          return (
            <button
              key={avatar.path}
              onClick={() => {
                if (isUnlocked) {
                  // Actually select the avatar
                  onAvatarSelect(avatar.path);
                  setPreviewAvatar(avatar.path);
                } else {
                  // Just preview the locked avatar requirements
                  setPreviewAvatar(avatar.path);
                }
              }}
              className={`group relative p-2 sm:p-3 rounded-2xl border-2 transition-all ${
                !isUnlocked 
                  ? isPreviewed
                    ? 'border-t-gray-500/60 border-l-gray-500/60 border-r-gray-700/80 border-b-gray-800/80 bg-gradient-to-b from-gray-800/40 via-gray-900/40 to-gray-950/40 backdrop-blur-sm opacity-90 cursor-pointer'
                    : 'border-t-gray-600/60 border-l-gray-600/60 border-r-gray-700/80 border-b-gray-800/80 bg-gradient-to-b from-gray-800/30 via-gray-900/30 to-gray-950/30 backdrop-blur-sm cursor-pointer hover:opacity-90 opacity-70'
                  : isSelected
                    ? 'border-t-blue-500/60 border-l-blue-500/60 border-r-blue-600/80 border-b-blue-700/80 bg-gradient-to-b from-blue-900/40 via-blue-950/40 to-blue-950/60 backdrop-blur-sm hover:scale-105'
                    : 'border-t-slate-500/60 border-l-slate-500/60 border-r-slate-700/80 border-b-slate-800/80 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm hover:scale-105 hover:border-t-blue-400/70 hover:border-l-blue-400/70 hover:border-r-blue-600/80 hover:border-b-blue-700/80'
              }`}
              style={{
                boxShadow: !isUnlocked
                  ? '0 6px 20px rgba(0, 0, 0, 0.7), 0 2px 8px rgba(0, 0, 0, 0.5), inset 0 2px 4px rgba(255, 255, 255, 0.05), inset 0 -3px 8px rgba(0, 0, 0, 0.6)'
                  : isSelected
                    ? '0 8px 24px rgba(59, 130, 246, 0.5), 0 2px 8px rgba(59, 130, 246, 0.3), inset 0 2px 4px rgba(59, 130, 246, 0.2), inset 0 -3px 8px rgba(30, 58, 138, 0.5)'
                    : '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
              }}
            >
              {/* Avatar Image */}
              <div className="relative mb-2">
                <img
                  src={avatar.path}
                  alt={avatar.name}
                  className={`w-full h-16 sm:h-20 md:h-24 object-contain sm:object-cover rounded-lg shadow-lg ${
                    !isUnlocked ? 'grayscale' : ''
                  }`}
                  style={{
                    filter: !isUnlocked 
                      ? 'grayscale(100%) brightness(0.5)'
                      : isSelected 
                        ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.9)) brightness(1.1)'
                        : 'drop-shadow(0 0 3px rgba(0, 0, 0, 0.5))'
                  }}
                />
                
                {/* Lock Icon for locked avatars */}
                {!isUnlocked && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="w-8 h-8 bg-red-600 rounded-full flex items-center justify-center shadow-lg">
                      <span className="text-white text-sm">🔒</span>
                    </div>
                  </div>
                )}
                
                {/* Selection Indicator */}
                {isSelected && isUnlocked && (
                  <div className="absolute -top-1 -right-1 w-6 h-6 bg-system-blue rounded-full flex items-center justify-center shadow-lg">
                    <span className="text-white text-xs font-bold">✓</span>
                  </div>
                )}
              </div>
              
              {/* Avatar Name */}
              <div className="text-center">
                <div className={`text-xs sm:text-sm font-semibold font-orbitron transition-colors leading-tight ${
                  !isUnlocked 
                    ? 'text-gray-500' 
                    : isSelected 
                      ? 'text-system-blue' 
                      : 'text-gray-300 group-hover:text-system-blue'
                }`}>
                  {avatar.name}
                </div>
                
                {/* Unlock Progress for locked avatars */}
                {!isUnlocked && avatar.requirements && (
                  <div className="mt-1">
                    <div className="text-xs text-gray-500">
                      {avatar.requirements.length === 1 
                        ? avatar.requirements[0].description
                        : `${avatar.requirements.length} Requirements`
                      }
                    </div>
                  </div>
                )}
              </div>
              
              {/* Hover Effect Overlay */}
              {isUnlocked && (
                <div className={`absolute inset-0 rounded-lg transition-opacity ${
                  isSelected 
                    ? 'bg-system-blue/10' 
                    : 'bg-system-blue/5 opacity-0 group-hover:opacity-100'
                }`} />
              )}
            </button>
          );
        })}
      </div>
      
      {/* Preview Section */}
      {previewAvatar && (
        <div 
          className="mt-4 p-3 sm:p-4 bg-gradient-to-b from-slate-800/40 via-slate-900/40 to-slate-950/40 backdrop-blur-sm border-t-2 border-l-2 border-slate-500/60 border-r-2 border-b-2 border-r-slate-700/80 border-b-slate-700/80 rounded-3xl"
          style={{
            boxShadow: '0 8px 24px rgba(0, 0, 0, 0.6), 0 2px 8px rgba(0, 0, 0, 0.4), inset 0 2px 4px rgba(255, 255, 255, 0.1), inset 0 -3px 8px rgba(0, 0, 0, 0.5)'
          }}
        >
          {(() => {
            const selectedAvatarData = avatars.find(a => a.path === previewAvatar);
            const isUnlocked = selectedAvatarData ? isAvatarUnlocked(selectedAvatarData) : false;
            
            return (
              <div className="flex items-start gap-2 sm:gap-3">
                <img
                  src={previewAvatar}
                  alt="Preview Avatar"
                  className="w-10 h-10 sm:w-12 sm:h-12 object-cover rounded-lg shadow-lg"
                  style={{
                    filter: isUnlocked 
                      ? 'drop-shadow(0 0 8px rgba(59, 130, 246, 0.9)) brightness(1.1)'
                      : 'grayscale(100%) brightness(0.5)'
                  }}
                />
                <div className="flex-1">
                  <div className={`text-xs sm:text-sm font-bold font-orbitron ${
                    isUnlocked ? 'text-system-blue' : 'text-gray-400'
                  }`}>
                    {selectedAvatarData?.name}
                  </div>
                  
                  {/* Always show requirements if they exist */}
                  {selectedAvatarData?.requirements && (
                    <div className="mt-2">
                      <div className={`text-xs font-rajdhani mb-2 ${
                        isUnlocked ? 'text-green-400' : 'text-gray-500'
                      }`}>
                        {isUnlocked 
                          ? (selectedAvatar === previewAvatar ? '✅ Requirements Met!' : '✅ Requirements Met! (Preview)')
                          : '🔒 Requirements to unlock:'
                        }
                      </div>
                      <div className="space-y-1">
                        {selectedAvatarData.requirements.map((req, index) => {
                          const currentValue = (() => {
                            switch (req.type) {
                              case 'level':
                                return userProfile.level;
                              case 'quests':
                                return userProfile.lifetimeStats.totalQuestsCompleted;
                              case 'streak':
                                return userProfile.longestStreak;
                              case 'currency':
                                return userProfile.lifetimeStats.totalCurrencyEarned;
                              default:
                                return 0;
                            }
                          })();
                          
                          const isMet = currentValue >= req.value;
                          const progress = Math.min((currentValue / req.value) * 100, 100);
                          
                          return (
                            <div key={index} className="flex items-center gap-2">
                              <div className="flex-1">
                                <div className="flex items-center justify-between text-xs">
                                  <span className={`font-rajdhani ${
                                    isMet ? 'text-green-400' : 'text-gray-400'
                                  }`}>
                                    {req.description}
                                  </span>
                                  <span className={`font-orbitron ${
                                    isMet ? 'text-green-400' : 'text-gray-500'
                                  }`}>
                                    {currentValue}/{req.value}
                                  </span>
                                </div>
                                <div className="w-full bg-gray-700 rounded-full h-1.5 mt-1">
                                  <div 
                                    className={`h-1.5 rounded-full transition-all duration-300 ${
                                      isMet ? 'bg-green-500' : 'bg-system-blue'
                                    }`}
                                    style={{ width: `${progress}%` }}
                                  />
                                </div>
                              </div>
                              {isMet && (
                                <span className="text-green-400 text-sm">✓</span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            );
          })()}
        </div>
      )}
    </div>
  );
};

export default AvatarSelector;
